import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Statistic, Row, Col, Button } from "antd";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import "./DetailView.css";
import DetailViewTable from "./DetailViewTable";
import {
  TEXT_CONSTANTS,
  FORMAT_COLORS,
  MATERIAL_COLORS,
  DATA_PACKAGING_FORMAT,
  DATA_PACKAGING_MATERIAL,
  STATISTIC,
  TABLE_DATA,
} from "../../constants/Constants";
import totalWeightIcon from "../../assets/icons/stat-info-1.png";
import rankingIcon from"../../assets/icons/quality.png"
import recyclableWeightIcon from "../../assets/icons/stat-info-2.png";
import emissionIcon from "../../assets/icons/stat-info-4.png";
import recyclableIcon from "../../assets/icons/stat-info-3.png";
import rollbackIcon from "../../assets/icons/rollback.png";
import ExcelTable from "./ExcelTable";
import { useSelector } from "react-redux";
import CarbonEmissionDetailView from "./CarbonEmissionDetailView";
import CarbonReportedDetailedView from "./CarbonReportedDetailedView";
import SupplierSubmittedGoalsDetailView from "./SupplierSubmittedGoalsDetailView";

const getStatisticsIcon = (title) => {
  let icons = null;
  if (title === "Total Weight") {
    icons = totalWeightIcon;
  } else if (title === "Recyclable Weight") {
    icons = recyclableWeightIcon;
  } 
  else if (title === "%Recyclable") {
    icons = recyclableWeightIcon;
  }
  else if (title === "Recyclability Percentage") {
    icons = recyclableIcon;
  }
  else if (title === "Emission Saved") {
    icons = emissionIcon;
  }
  else if(title=="Supplier Rank")
  {
    icons=rankingIcon;
  }

  return icons;
};

const getColorBox = (type, colors, index) => {
  let colorBox = null;
  if (type === "format") {
    colorBox = (
      <span
        className="legend-color mx-4 my-2 border border-black-100"
        style={{
          backgroundColor: colors[index % colors.length],
          border: "1px solid #010101",
        }}
      />
    );
  } else if (type === "material") {
    colorBox = (
      <span
        className="legend-color mx-4 my-2 w-[20px] h-[20px]"
        style={{
          backgroundColor: colors[index % colors.length],
          border: "1px solid #010101",
        }}
      />
    );
  }
  return colorBox;
};

const CustomLegend = ({ payload, colors, type }) => {
  return (
    <div className="custom-legend">
      <span className="text-center text-xs">Legend</span>
      {payload.map((entry, index) => (
        <div key={`item-${index}`} className="legend-item">
          {getColorBox(type, colors, index)}
          <span className="text-xs">{entry.name}</span>
        </div>
      ))}
    </div>
  );
};

const DetailView = () => {
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = location;
  const [data,setData]=useState("");
  const role=sessionStorage.getItem("Role")
  const{recyclePackaging,carbonEmissionAvoided,carbonReported, supplierSubmittedGoals } = useSelector((state) => state.dashboard);
  
  useEffect(() => {
    if (state && state.kpiName) {
      switch(state.kpiName) {
        case "Recycle Packaging":
          setData(recyclePackaging);
          break;
        case "Carbon Emission Avoided":
          setData(carbonEmissionAvoided);
          break;
        case "Carbon Emission Reported CDP":
          setData(carbonReported);
          break;
        case "Supplier Reported Goals":
          setData(supplierSubmittedGoals);
          break;
        default:
          setData(null);
      }
    }
  }, [state, recyclePackaging, carbonEmissionAvoided, carbonReported, supplierSubmittedGoals]);

  const RECYCLE_PACKAGING_FORMAT = recyclePackaging ? Object.entries(recyclePackaging.formatDistribution)?.map(([name, value]) => ({
    name,
    value
  })) : DATA_PACKAGING_FORMAT;
  
  const RECYCLE_PACKAGING_MATERIAL = recyclePackaging ? Object.entries(recyclePackaging.materialDistribution)?.map(([name, value]) => ({
    name,
    value
  })) : DATA_PACKAGING_MATERIAL;

  const STATISTICS = recyclePackaging ? [
    {
      title: "Total Weight",
      value: `${recyclePackaging.totalWeight} MT`,
    },
    {
      title: "Recyclable Weight",
      value: `${recyclePackaging.recyclableWeight} MT`,
    },
    {
      title: "Recyclability Percentage",
      value: `${recyclePackaging.recyclabilityPercentage} %`,
    },
    {
      title: "Emission Saved",
      value: `${recyclePackaging.totalEmissionSaved} MTCO2`,
    },
    ...(role == "System Admin" ? [] : [{
      title: "Supplier Rank",
      value: (`${recyclePackaging.supplierRank}/${recyclePackaging.totalRank}`)||0
    }])
  ] : STATISTIC;

  const handleBackClick = () => {
    const basePath = location.pathname.includes("/admin")
    ? "/admin"
    : "/supplier";
    navigate(`${basePath}/dashboard`);
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);


  if (state && state.kpiName === "Carbon Emission Avoided") {
    return <CarbonEmissionDetailView 
      data={carbonEmissionAvoided} 
      handleBackClick={handleBackClick} 
      kpiName={state.kpiName}
      role={role}
    />;
  }

  if (state && state.kpiName === "Suppliers Submitted Goals") {
    return <SupplierSubmittedGoalsDetailView
      data={supplierSubmittedGoals}
      handleBackClick={handleBackClick}
      kpiName={state.kpiName}
      role={role}
    />;
  }
  if (state && state.kpiName === "CDP Carbon Emission Reported") {
    return <CarbonReportedDetailedView
      report={carbonReported}
      handleBackClick={handleBackClick}
      kpiName={state.kpiName}
      role={role}
    />;
  }
  return (
    <div className="md:p-0 p-2">
      <div className="detail-view-header flex justify-between items-center mb-4 md:flex-row flex-col-reverse">
        <div className="heading text-2xl font-medium text-[#004481]">
          {TEXT_CONSTANTS.DETAIL_VIEW_TITLE}
          {state && state.kpiName}
        </div>
        <Button
          shape="round"
          icon={<img src={rollbackIcon || "/placeholder.svg"} alt={"back"} className="w-10 h-5" />}
          size="60px"
          className="top-[3px] w-[100px] h-[30px] bg-[#F8FAFC] self-end"
          style={{ border: "1px solid #A1BAD1" }}
          onClick={handleBackClick}
        >
          Back
        </Button>
      </div>

      <div className="outer-card p-4 rounded-2xl shadow-md bg-[#F8FAFC] mb-6">
        <Row gutter={[16, 16]}>
          {STATISTICS?.map((stat, index) => {
            return (
              <Col key={index} xs={24} sm={12} md={role == "System Admin" ? 8 : 7} lg={role == "System Admin" ? 6 : 8}>
                <div className="statistic-card p-4 rounded-2xl bg-white ml-2">
                  <img
                    src={stat && getStatisticsIcon(stat?.title)}
                    alt="icon" 
                    className="md:w-8 md:h-8 w-6 h-6"
                  />
                  <Statistic
                    title={
                      <span className="statistic-title text-sm">
                        {stat?.title}
                      </span>
                    }
                    value={stat?.value}
                    valueStyle={{
                      color: "rgb(1 77 78)",
                      fontSize: isMobile ? "20px" : "20px",
                      fontWeight: 600,
                      textAlign: "left",
                      marginLeft: "10px",
                    }}
                  />
                </div>
              </Col>
            );
          })}
        </Row>
      </div>

      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
        <Col xs={{ span: 24, order: 2 }} xl={{ span: 12, order: 1 }}>
          <div className="chart-card rounded-2xl shadow-md bg-white mb-6">
            <div className="flex-container flex flex-col md:flex-row">
              <div className="chart-container w-full md:w-2/3">
                <h3 className="chart-header text-sm font-medium mb-4 text-[#000000]">
                  {TEXT_CONSTANTS.PACKAGING_FORMAT_TITLE}
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                    <Pie
                      data={RECYCLE_PACKAGING_FORMAT}
                      cx="60%"
                      cy="40%"
                      outerRadius="80%"
                      innerRadius="20%"
                      fill="#8884d8"
                      dataKey="value"
                      label
                    >
                      {RECYCLE_PACKAGING_FORMAT.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={FORMAT_COLORS[index % FORMAT_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `${value}%`} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="legend-container ml-0 md:ml-4 mt-4 md:mt-0 w-auto">
                <CustomLegend
                  payload={RECYCLE_PACKAGING_FORMAT}
                  colors={FORMAT_COLORS}
                  type="format"
                />
              </div>
            </div>
          </div>
        </Col>
        <Col xs={{ span: 24, order: 2 }} xl={{ span: 12, order: 1 }}>
          <div className="chart-card rounded-2xl shadow-md bg-white mb-6">
            <div className="flex-container flex flex-col md:flex-row">
              <div className="chart-container w-full md:w-2/3">
                <h3 className="chart-header text-sm font-medium mb-4 text-[#000000]">
                  {TEXT_CONSTANTS.PACKAGING_MATERIAL_TITLE}
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                    <Pie
                      data={RECYCLE_PACKAGING_MATERIAL}
                      cx="60%"
                      cy="40%"
                      outerRadius="80%"
                      innerRadius="20%"
                      fill="#8884d8"
                      dataKey="value"
                      label
                    >
                      {RECYCLE_PACKAGING_MATERIAL.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={MATERIAL_COLORS[index % MATERIAL_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `${value}%`} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="ml-0 md:ml-4 mt-4 md:mt-0 w-auto">
                <CustomLegend
                  payload={RECYCLE_PACKAGING_MATERIAL}
                  colors={MATERIAL_COLORS}
                  type="material"
                />
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <div className="flex justify-end mr-2 mb-2">
        <ExcelTable tableData={data?.tableData} title="Recycle Packaging"/>
      </div>
      
      <DetailViewTable tableData={data?.tableData || TABLE_DATA} role={role} />
    </div>
  );
};

export default DetailView;